import { createUnifiedTheme, genPageTheme, palettes } from '@backstage/theme';
import type { UnifiedTheme } from '@backstage/theme';

// Official Kyndryl Brand Colors from Brand Standards V1.9
export const kyndrylColors = {
  // Hero Color
  warmRed: '#FF462D',
  
  // Supporting Colors
  deepForest: '#042315',
  spruce: '#29707A',
  darkStone: '#3D3C3C',
  
  // Complementary Colors
  springGreen: '#4CDD84',
  earth: '#9E9287',
  cloud: '#F2F1EE',
  skye: '#E4F4F1',
  white: '#FFFFFF',
  
  // Digital Only
  digitalDarkSpringGreen: '#187E3F',
  digitalDarkEarth: '#565049',
};

// ---------------------------------------------------------------------------
// Kyndryl "K" Brand Line Art Pattern
// Overlapping K letterforms inspired by Kyndryl brand identity.
// Two interleaved sets of K's with stems every 100px, diags spanning 200px.
// 100px horizontal offset between / and \ at vertex, 0px vertical.
// Canvas: 1368×200, compact 110px band (y=45–155), no leftmost stem.
// Rendered at fixed 200px height, center-cropped at smaller header sizes.
// ---------------------------------------------------------------------------
const kyndrylKPattern = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1368' height='200'%3E%3Cg stroke='rgba(255,70,45,0.25)' stroke-width='2' fill='none'%3E%3Cline x1='900' y1='100' x2='1100' y2='45'/%3E%3Cline x1='950' y1='100' x2='1100' y2='155'/%3E%3Cline x1='1100' y1='45' x2='1100' y2='155'/%3E%3Cline x1='1100' y1='100' x2='1300' y2='45'/%3E%3Cline x1='1150' y1='100' x2='1300' y2='155'/%3E%3Cline x1='1300' y1='45' x2='1300' y2='155'/%3E%3Cline x1='1300' y1='100' x2='1500' y2='45'/%3E%3Cline x1='1350' y1='100' x2='1500' y2='155'/%3E%3Cline x1='1500' y1='45' x2='1500' y2='155'/%3E%3Cline x1='1500' y1='100' x2='1700' y2='45'/%3E%3Cline x1='1550' y1='100' x2='1700' y2='155'/%3E%3Cline x1='1000' y1='45' x2='1000' y2='155'/%3E%3Cline x1='1000' y1='100' x2='1200' y2='45'/%3E%3Cline x1='1050' y1='100' x2='1200' y2='155'/%3E%3Cline x1='1200' y1='45' x2='1200' y2='155'/%3E%3Cline x1='1200' y1='100' x2='1400' y2='45'/%3E%3Cline x1='1250' y1='100' x2='1400' y2='155'/%3E%3Cline x1='1400' y1='45' x2='1400' y2='155'/%3E%3Cline x1='1400' y1='100' x2='1600' y2='45'/%3E%3Cline x1='1450' y1='100' x2='1600' y2='155'/%3E%3C/g%3E%3C/svg%3E")`;

// Secondary variant — slightly different opacity
const kyndrylKPattern2 = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1368' height='200'%3E%3Cg stroke='rgba(255,70,45,0.2)' stroke-width='2' fill='none'%3E%3Cline x1='900' y1='100' x2='1100' y2='45'/%3E%3Cline x1='950' y1='100' x2='1100' y2='155'/%3E%3Cline x1='1100' y1='45' x2='1100' y2='155'/%3E%3Cline x1='1100' y1='100' x2='1300' y2='45'/%3E%3Cline x1='1150' y1='100' x2='1300' y2='155'/%3E%3Cline x1='1300' y1='45' x2='1300' y2='155'/%3E%3Cline x1='1300' y1='100' x2='1500' y2='45'/%3E%3Cline x1='1350' y1='100' x2='1500' y2='155'/%3E%3Cline x1='1500' y1='45' x2='1500' y2='155'/%3E%3Cline x1='1500' y1='100' x2='1700' y2='45'/%3E%3Cline x1='1550' y1='100' x2='1700' y2='155'/%3E%3Cline x1='1000' y1='45' x2='1000' y2='155'/%3E%3Cline x1='1000' y1='100' x2='1200' y2='45'/%3E%3Cline x1='1050' y1='100' x2='1200' y2='155'/%3E%3Cline x1='1200' y1='45' x2='1200' y2='155'/%3E%3Cline x1='1200' y1='100' x2='1400' y2='45'/%3E%3Cline x1='1250' y1='100' x2='1400' y2='155'/%3E%3Cline x1='1400' y1='45' x2='1400' y2='155'/%3E%3Cline x1='1400' y1='100' x2='1600' y2='45'/%3E%3Cline x1='1450' y1='100' x2='1600' y2='155'/%3E%3C/g%3E%3C/svg%3E")`;

// ---------------------------------------------------------------------------
// Shared MUI + Backstage component overrides (used by both themes)
// Uses MUI v5 `components` format compatible with createUnifiedTheme
// ---------------------------------------------------------------------------
const kyndrylComponentOverrides = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        textTransform: 'none' as const,
        fontFamily: '"TWK Everett", sans-serif',
        fontWeight: 500,
        letterSpacing: '0.01em',
      },
      containedPrimary: {
        '&:hover': {
          backgroundColor: kyndrylColors.springGreen,
          color: kyndrylColors.deepForest,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
        },
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      title: {
        fontSize: '1.05rem',
        fontWeight: 600,
      },
      subheader: {
        fontSize: '0.8rem',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontSize: '0.7rem',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        fontSize: '0.75rem',
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:nth-of-type(even)': {
          backgroundColor: 'rgba(24, 126, 63, 0.03)',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: kyndrylColors.digitalDarkSpringGreen,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: kyndrylColors.digitalDarkSpringGreen,
        height: 3,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        fontFamily: '"TWK Everett", sans-serif',
        fontWeight: 500,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      barColorPrimary: {
        backgroundColor: kyndrylColors.digitalDarkSpringGreen,
      },
    },
  },
  MuiCircularProgress: {
    styleOverrides: {
      colorPrimary: {
        color: kyndrylColors.digitalDarkSpringGreen,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: '12px',
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontFamily: '"TWK Everett", sans-serif',
        fontSize: '0.75rem',
        borderRadius: '6px',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '16px',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
      },
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: {
        '&.Mui-active': {
          color: kyndrylColors.digitalDarkSpringGreen,
        },
        '&.Mui-completed': {
          color: kyndrylColors.springGreen,
        },
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: {
        fontFamily: '"TWK Everett", sans-serif',
        '&.Mui-active': {
          fontWeight: 600,
        },
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      line: {
        borderColor: kyndrylColors.digitalDarkSpringGreen,
      },
    },
  },
  // Override Backstage Header to render K art at fixed 200px, right-aligned, center-cropped
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundSize: 'auto 200px, cover',
        backgroundPosition: 'right center, center',
        backgroundRepeat: 'no-repeat, no-repeat',
        boxShadow: 'none',
      },
    },
  },
  BackstageItemCardHeader: {
    styleOverrides: {
      root: {
        backgroundSize: 'auto 200px, cover',
        backgroundPosition: 'right center, center',
        backgroundRepeat: 'no-repeat, no-repeat',
      },
    },
  },
};

export const kyndrylDarkTheme: UnifiedTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    mode: 'dark',
    primary: {
      main: kyndrylColors.digitalDarkSpringGreen, // Digital Dark Spring Green - more refined
      light: kyndrylColors.springGreen,
      dark: '#0D5C2F', // Darker variant
    },
    secondary: {
      main: kyndrylColors.spruce,
      light: '#3A8593',
      dark: '#1D5560',
    },
    error: {
      main: kyndrylColors.warmRed,
    },
    background: {
      default: '#000000', // True black for sleeker look
      paper: '#0A0F0D', // Very dark forest, almost black
    },
    navigation: {
      background: '#000000',
      indicator: kyndrylColors.digitalDarkSpringGreen,
      color: kyndrylColors.cloud,
      selectedColor: kyndrylColors.springGreen,
      navItem: {
        hoverBackground: 'rgba(24, 126, 63, 0.08)',
      },
    },
    action: {
      active: kyndrylColors.digitalDarkSpringGreen,
      hover: 'rgba(24, 126, 63, 0.08)',
      selected: 'rgba(24, 126, 63, 0.12)',
      disabled: 'rgba(242, 241, 238, 0.3)',
      disabledBackground: 'rgba(242, 241, 238, 0.12)',
    },
    text: {
      primary: kyndrylColors.cloud, // Softer white for less eye strain
      secondary: '#A8A29D', // Muted earth tone
    },
    banner: {
      info: kyndrylColors.digitalDarkSpringGreen,
      error: kyndrylColors.warmRed,
      text: kyndrylColors.cloud,
      link: kyndrylColors.springGreen,
    },
    border: '#1A1F1D', // Very subtle borders
    divider: 'rgba(76, 221, 132, 0.08)', // Subtle green tint
    status: {
      ok: kyndrylColors.digitalDarkSpringGreen,
      warning: kyndrylColors.warmRed,
      error: kyndrylColors.warmRed,
      running: kyndrylColors.spruce,
      pending: kyndrylColors.earth,
      aborted: kyndrylColors.digitalDarkEarth,
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    documentation: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern2 }),
    tool: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern2 }),
    service: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    website: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    library: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern2 }),
    other: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    app: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
    apis: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern2 }),
    card: genPageTheme({ colors: ['#000000', '#000000'], shape: kyndrylKPattern }),
  },
  fontFamily: '"TWK Everett", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
  components: {
    ...kyndrylComponentOverrides,
    // Dark-theme specific: remove card borders
    MuiCard: {
      ...kyndrylComponentOverrides.MuiCard,
      styleOverrides: {
        ...kyndrylComponentOverrides.MuiCard.styleOverrides,
        root: {
          ...kyndrylComponentOverrides.MuiCard.styleOverrides.root,
          border: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          },
        },
      },
    },
  },
  typography: {
    fontFamily: '"TWK Everett", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
    htmlFontSize: 16,
    h1: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 700,
      fontSize: '3rem',
      marginBottom: 16,
    },
    h2: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '2.25rem',
      marginBottom: 14,
    },
    h3: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.75rem',
      marginBottom: 12,
    },
    h4: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      marginBottom: 10,
    },
    h5: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      marginBottom: 8,
    },
    h6: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
      marginBottom: 6,
    },
  },
});

export const kyndrylLightTheme: UnifiedTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: kyndrylColors.digitalDarkSpringGreen,
      light: kyndrylColors.springGreen,
      dark: kyndrylColors.deepForest,
    },
    secondary: {
      main: kyndrylColors.spruce,
      light: kyndrylColors.skye,
      dark: kyndrylColors.deepForest,
    },
    error: {
      main: kyndrylColors.warmRed,
    },
    background: {
      default: kyndrylColors.cloud, // Cloud as main background
      paper: kyndrylColors.white,
    },
    navigation: {
      background: kyndrylColors.white,
      indicator: kyndrylColors.digitalDarkSpringGreen,
      color: kyndrylColors.deepForest,
      selectedColor: kyndrylColors.digitalDarkSpringGreen,
      navItem: {
        hoverBackground: kyndrylColors.skye,
      },
    },
    action: {
      active: kyndrylColors.digitalDarkSpringGreen,
      hover: 'rgba(24, 126, 63, 0.08)',
      selected: 'rgba(24, 126, 63, 0.16)',
    },
    text: {
      primary: kyndrylColors.deepForest,
      secondary: kyndrylColors.digitalDarkEarth,
    },
    border: kyndrylColors.earth,
    status: {
      ok: kyndrylColors.digitalDarkSpringGreen,
      warning: kyndrylColors.warmRed,
      error: kyndrylColors.warmRed,
      running: kyndrylColors.spruce,
      pending: kyndrylColors.earth,
      aborted: kyndrylColors.darkStone,
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern, options: { fontColor: kyndrylColors.deepForest } }),
    documentation: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern2, options: { fontColor: kyndrylColors.deepForest } }),
    tool: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern2, options: { fontColor: kyndrylColors.deepForest } }),
    service: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern, options: { fontColor: kyndrylColors.deepForest } }),
    website: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern, options: { fontColor: kyndrylColors.deepForest } }),
    library: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern2, options: { fontColor: kyndrylColors.deepForest } }),
    other: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern, options: { fontColor: kyndrylColors.deepForest } }),
    app: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern, options: { fontColor: kyndrylColors.deepForest } }),
    apis: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern2, options: { fontColor: kyndrylColors.deepForest } }),
    card: genPageTheme({ colors: [kyndrylColors.cloud, kyndrylColors.cloud], shape: kyndrylKPattern, options: { fontColor: kyndrylColors.deepForest } }),
  },
  fontFamily: '"TWK Everett", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
  components: {
    ...kyndrylComponentOverrides,
  },
  typography: {
    fontFamily: '"TWK Everett", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
    htmlFontSize: 16,
    h1: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 700,
      fontSize: '3rem',
      marginBottom: 16,
    },
    h2: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '2.25rem',
      marginBottom: 14,
    },
    h3: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.75rem',
      marginBottom: 12,
    },
    h4: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      marginBottom: 10,
    },
    h5: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      marginBottom: 8,
    },
    h6: {
      fontFamily: '"TWK Everett", sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
      marginBottom: 6,
    },
  },
});
